import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

const monthNames = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const toDateValue = (date) => {
	const year = date.getFullYear();
	const month = `${date.getMonth() + 1}`.padStart(2, "0");
	const day = `${date.getDate()}`.padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const parseValue = (value) => {
	if (!value) return null;
	const date = new Date(`${value}T00:00:00`);
	return Number.isNaN(date.getTime()) ? null : date;
};

const getCalendarDays = (monthDate) => {
	const year = monthDate.getFullYear();
	const month = monthDate.getMonth();
	const firstDay = new Date(year, month, 1).getDay();
	const daysInMonth = new Date(year, month + 1, 0).getDate();
	const previousMonthDays = new Date(year, month, 0).getDate();
	const days = [];

	for (let index = firstDay - 1; index >= 0; index -= 1) {
		days.push({
			date: new Date(year, month - 1, previousMonthDays - index),
			isCurrentMonth: false,
		});
	}

	for (let day = 1; day <= daysInMonth; day += 1) {
		days.push({ date: new Date(year, month, day), isCurrentMonth: true });
	}

	while (days.length % 7 !== 0) {
		const nextDay = days.length - firstDay - daysInMonth + 1;
		days.push({
			date: new Date(year, month + 1, nextDay),
			isCurrentMonth: false,
		});
	}

	return days;
};

const ModernDateInput = ({
	name,
	label,
	value,
	onChange,
	onBlur,
	required,
	error,
	disabled,
}) => {
	const { t } = useTranslation();
	const selectedDate = parseValue(value);
	const initialMonth = selectedDate || new Date();
	const [isOpen, setIsOpen] = useState(false);
	const [visibleMonth, setVisibleMonth] = useState(
		new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1)
	);
	const wrapperRef = useRef(null);

	const calendarDays = useMemo(
		() => getCalendarDays(visibleMonth),
		[visibleMonth]
	);

	const displayValue = selectedDate
		? selectedDate.toLocaleDateString(undefined, {
				day: "2-digit",
				month: "short",
				year: "numeric",
		  })
		: "";

	const emitChange = (date) => {
		const nextValue = toDateValue(date);
		const event = {
			target: {
				id: name,
				name,
				type: "date",
				value: nextValue,
				required,
				validity: {
					valueMissing: required && !nextValue,
					typeMismatch: false,
				},
			},
		};
		onChange(event);
		setTimeout(() => onBlur(event), 0);
	};

	const handleSelect = (date) => {
		emitChange(date);
		setIsOpen(false);
	};

	const handleBlur = (event) => {
		if (!wrapperRef.current?.contains(event.relatedTarget)) {
			const blurEvent = {
				target: {
					id: name,
					name,
					type: "date",
					value: value || "",
					required,
					validity: {
						valueMissing: required && !value,
						typeMismatch: false,
					},
				},
			};
			onBlur(blurEvent);
		}
	};

	return (
		<div
			className="form-group child-register-field modern-date-field"
			ref={wrapperRef}
			onBlur={handleBlur}
		>
			<label htmlFor={name}>
				{t(label)} {required ? <span style={{ color: "red" }}>*</span> : null}
			</label>
			<button
				type="button"
				id={name}
				className={`modern-date-trigger${displayValue ? "" : " is-empty"}`}
				disabled={disabled}
				onClick={() => setIsOpen((current) => !current)}
			>
				<span>{displayValue || t("Select date")}</span>
				<CalendarTodayIcon />
			</button>
			{isOpen ? (
				<div className="modern-date-popover" tabIndex="-1">
					<div className="modern-date-header">
						<button
							type="button"
							onClick={() =>
								setVisibleMonth(
									new Date(
										visibleMonth.getFullYear(),
										visibleMonth.getMonth() - 1,
										1
									)
								)
							}
						>
							<ChevronLeftIcon />
						</button>
						<strong>{`${t(monthNames[visibleMonth.getMonth()])} ${visibleMonth.getFullYear()}`}</strong>
						<button
							type="button"
							onClick={() =>
								setVisibleMonth(
									new Date(
										visibleMonth.getFullYear(),
										visibleMonth.getMonth() + 1,
										1
									)
								)
							}
						>
							<ChevronRightIcon />
						</button>
					</div>
					<div className="modern-date-grid">
						{dayNames.map((day) => (
							<span className="modern-date-day-name" key={day}>
								{t(day)}
							</span>
						))}
						{calendarDays.map(({ date, isCurrentMonth }) => {
							const dateValue = toDateValue(date);
							const isSelected = value === dateValue;
							const isToday = toDateValue(new Date()) === dateValue;
							return (
								<button
									type="button"
									key={dateValue}
									className={[
										"modern-date-day",
										isCurrentMonth ? "" : "is-muted",
										isSelected ? "is-selected" : "",
										isToday ? "is-today" : "",
									]
										.filter(Boolean)
										.join(" ")}
									onClick={() => handleSelect(date)}
								>
									{date.getDate()}
								</button>
							);
						})}
					</div>
				</div>
			) : null}
			{error && (value === "" || required) ? (
				<p className="red-text m-0">{t("Required field.")}</p>
			) : null}
		</div>
	);
};

export default ModernDateInput;
